import { AsyncPipe } from '@angular/common';
import { Component, HostListener, Input, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';

import { BetBuilderButtonSource, EventModel, SetTab } from '@frontend/sports/betting-offer/feature/model';
import { SitecoreEventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { ThemeOptions } from '@frontend/sports/common/ui/shared';
import { SportsThemedButtonDirective } from '@frontend/sports/common/ui/sports-themed-button';
import { ModalRef } from '@frontend/sports/modal/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { MarketUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { DsButton, DsButtonKind, DsButtonSize, DsButtonVariant } from '@frontend/ui/button';
import { map } from 'rxjs';

import { DispatcherEvent } from '../../../event-details-common/event-details-common.models';
import { BetBuilderTrackingValues } from '../../bet-builder-tracking';
import { BetBuilderConfigService } from '../../services/bet-builder-config.service';
import { BetBuilderOverlayService } from '../../services/bet-builder-overlay.service';
import { getBetbuilderTypeValue } from '../../utils';
import { BetBuilderOverlayHeaderComponent } from '../bet-builder-overlay-header/bet-builder-overlay-header.component';

@Component({
    selector: 'ms-bet-builder-button',
    templateUrl: './bet-builder-button.component.html',
    imports: [AsyncPipe, BetBuilderOverlayHeaderComponent, DsButton, SportsThemedButtonDirective],
})
export class BetBuilderButtonComponent implements OnInit {
    private tracker = inject(TrackingService);
    private redirectService = inject(RedirectHelperService);
    sitecoreEventDetails = inject(SitecoreEventDetailsConfig);
    private betBuilderConfigService = inject(BetBuilderConfigService);
    private urlHelper = inject(UrlHelperService);
    private betBuilderOverlayService = inject(BetBuilderOverlayService);
    private dispatcherService = inject(DispatcherService);

    private _buttonSource = BetBuilderButtonSource.Event;

    @Input() event: EventModel;
    @Input() hasPreCreated?: boolean;
    @Input() allowBetslipOpening = true;

    @Input()
    set buttonSource(buttonSource: BetBuilderButtonSource | undefined) {
        this._buttonSource = buttonSource ?? this._buttonSource;
    }

    get buttonSource(): BetBuilderButtonSource {
        return this._buttonSource;
    }

    @Input() isPrecreatedBABPod = false;
    @Input() betBuilderEpcotEnabled = false;
    @Input() source?: string;

    buttonSources = BetBuilderButtonSource;

    textWrap: boolean;
    modal: ModalRef;
    @ViewChild('header') headerTemplate: TemplateRef<any>;
    readonly btnText$ = this.sitecoreEventDetails.whenReady.pipe(
        map(() => {
            return this.isPrecreatedBABPod
                ? this.sitecoreEventDetails.eventDetails.PrecreatedBuildABetCTAText
                : this.sitecoreEventDetails.eventDetails.BetBuilderButtonName;
        }),
    );

    buttonThemeKind: ThemeOptions<DsButtonKind>;
    buttonThemeVariant: ThemeOptions<DsButtonVariant>;
    buttonThemeSize: ThemeOptions<DsButtonSize>;

    ngOnInit(): void {
        this.textWrap = this._buttonSource === BetBuilderButtonSource.Coupons;
        this.buttonThemeKind = { 'mgm-dice': 'secondary', 'sports-interaction': 'secondary' };
        this.buttonThemeVariant = { 'mgm-dice': 'outline', 'sports-interaction': 'outline' };
        this.buttonThemeSize = this.betBuilderEpcotEnabled
            ? { 'mgm-dice': 'medium', 'sports-interaction': 'medium' }
            : { 'mgm-dice': 'large', 'sports-interaction': 'large' };
    }

    @HostListener('click', ['$event'])
    clicked(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }

    openBetBuilder(): void {
        if (this.betBuilderConfigService.useBetBuilderInEntainUI(this.event.sport.id)) {
            if (this.source || this._buttonSource === BetBuilderButtonSource.Coupons) {
                this.tracker.track(trackingConstants.EVENT_TRACKING, {
                    [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetBuilderTrackingValues.BetBuilder,
                    [trackingConstants.COMPONENT_LABEL_EVENT]: BetBuilderTrackingValues.BuildaBetPod,
                    [trackingConstants.COMPONENT_ACTION_EVENT]: BetBuilderTrackingValues.Click,
                    [trackingConstants.COMPONENT_POSITION_EVENT]:
                        this._buttonSource === BetBuilderButtonSource.Coupons ? BetBuilderTrackingValues.Coupon : this.source,
                    [trackingConstants.COMPONENT_LOCATION_EVENT]: this.event.name,
                    [trackingConstants.COMPONENT_EVENT_DETAILS]: BetBuilderTrackingValues.BuildaBet,
                    [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
                    [trackingConstants.SPORT_ID]: this.event.sport.id.toString(),
                    [trackingConstants.SPORT_LEAGUE_ID]: this.event.league.id.toString(),
                });
            }

            if (this.betBuilderConfigService.showEntainUiInOverlay(this.event.sport.id)) {
                this.openEntainBetBuilderOverlay();
            } else {
                this.redirectToBetBuilderMarketList();
            }
        }
    }

    openEntainBetBuilderOverlay() {
        this.betBuilderOverlayService.openBuilder({
            headerTemplate: this.headerTemplate,
            event: this.event,
            source: BetBuilderTrackingValues.BuildaBetButton,
            isOverlay: true,
        });
    }

    redirectToBetBuilderMarketList(): void {
        switch (this._buttonSource) {
            case BetBuilderButtonSource.Event:
                this.dispatcherService.dispatch<SetTab>(DispatcherEvent.EventDetails.SetTab, getBetbuilderTypeValue(this.event, SetTab));
                break;
            case BetBuilderButtonSource.Coupons:
            case BetBuilderButtonSource.TopEvents:
            default:
                const eventUrl = this.urlHelper.getEventUrl(this.event, getBetbuilderTypeValue(this.event, MarketUrlParam));
                this.redirectService.goToPage(eventUrl);
                break;
        }
    }

    get locationEvent(): string {
        switch (this._buttonSource) {
            case BetBuilderButtonSource.Coupons:
                return 'coupons';
            case BetBuilderButtonSource.Event:
                return 'Event page';
            case BetBuilderButtonSource.TopEvents:
                return 'Top Events';
            default:
                return NOT_APPLICABLE;
        }
    }
}
